import { IconButton, OutlineButton } from "components/button";
import { ChecklistExistingPicker } from "components/checklist_dropdown";
import { Input } from "components/input";
import * as React from "react";
import { useRef, useState } from "react";
import { CgUndo } from "react-icons/cg";
import {
  RiArrowDownSLine,
  RiArrowRightSLine,
  RiCloseCircleFill,
  RiDeleteBin2Fill,
} from "react-icons/ri";
import { useTranslation } from "react-i18next";
import type { ChecklistsApi, ChecklistRow } from "./useChecklistRows";

type Props = {
  /** The hook's return — owns state, queries, and `flushPending`. */
  checklists: ChecklistsApi;
  /** Raw form value, passed straight from the modal's `formik.values.fk_appointment_type_id`. */
  fkAppointmentTypeId: string;
  /** Effective type id (>0 finite) or null. */
  actualTypeOrNull: number | null;
  /** Effective default id, or null for parent. */
  defaultNum: number | null;
  /** Dropdown mode — drives the "(No auto-fill)" hint visibility. */
  selectedDefaultMode?: string | null;
  /** True when the modal shows the sub-type / default Field. */
  hasSubAppointmentDefaultPicker?: boolean;
  isCompleted: boolean;
};

/**
 * Pure presenter for the appointment-modal checklist panel. State, queries,
 * mutations and `flushPending` all live in `useChecklistRows` (called by the
 * parent). This file owns only ephemeral UI state — the picker open flag and
 * the panel collapsed/expanded flag.
 */
export function AppointmentModalChecklistsSection({
  checklists,
  fkAppointmentTypeId,
  actualTypeOrNull,
  defaultNum,
  selectedDefaultMode = null,
  hasSubAppointmentDefaultPicker = true,
  isCompleted,
}: Props) {
  const { t } = useTranslation();
  const tBase = "containers.appointmentCard.appointment-folder.appointmentModal";
  const tr = React.useCallback(
    (key: string, options?: Record<string, string | number>) =>
      t(`${tBase}.${key}`, options),
    [t]
  );

  const [checklistRowsExpanded, setChecklistRowsExpanded] = useState(false);
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  const templatePickerAnchorRef = useRef<HTMLDivElement | null>(null);

  const {
    displayRows,
    scopedTemplatesNotYetAdded,
    isLoading,
    isError,
    templatesIsLoading,
    handleLabelChange,
    handleRequiredChange,
    handleDismissRow,
    handleUndoPatchesForRow,
    handleConfirmTemplatesFromPicker,
  } = checklists;

  const typeNum = Number(fkAppointmentTypeId);
  const readOnly = isCompleted || (actualTypeOrNull != null && isLoading);

  return (
    <div className="mt-1 mb-2 px-1">
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <h2 className="text-lg font-semibold m-0">{tr("Checklists on appointment")}</h2>
      </div>
      {readOnly && isCompleted ? (
        <p className="text-sm text-gray-600 mb-2">{tr("Checklists read-only completed")}</p>
      ) : null}
      {!Number.isFinite(typeNum) || fkAppointmentTypeId === "" ? (
        <p className="text-sm text-gray-600">{tr("Checklists pick type hint")}</p>
      ) : null}
      {!readOnly &&
      hasSubAppointmentDefaultPicker &&
      selectedDefaultMode === "new" &&
      Number.isFinite(typeNum) &&
      scopedTemplatesNotYetAdded.length > 0 &&
      !templatesIsLoading ? (
        <p className="text-sm text-blue-800 bg-blue-50 border border-blue-100 rounded-md px-2 py-2 mb-2">
          {tr("Checklists no autofill templates hint", {
            count: scopedTemplatesNotYetAdded.length,
          })}
        </p>
      ) : null}
      {isError ? (
        <p className="text-sm text-red-600">{tr("Checklists load error")}</p>
      ) : null}
      <div className="border border-gray-200 rounded-md overflow-hidden">
        <button
          type="button"
          className="flex w-full items-center justify-between gap-2 px-2 py-2 text-left text-sm font-medium text-gray-800 bg-gray-50 hover:bg-gray-100 border-b border-gray-200"
          onClick={() => setChecklistRowsExpanded((v) => !v)}
          aria-expanded={checklistRowsExpanded}
        >
          <span className="flex min-w-0 items-center gap-2">
            {checklistRowsExpanded ? (
              <RiArrowDownSLine className="shrink-0" size={18} aria-hidden />
            ) : (
              <RiArrowRightSLine className="shrink-0" size={18} aria-hidden />
            )}
            <span className="truncate">
              {tr("Checklists rows panel")}
              {!isLoading ? ` (${displayRows.length})` : ""}
            </span>
          </span>
        </button>
        {checklistRowsExpanded ? (
          <div className="flex flex-col gap-2 p-2">
            {displayRows.length === 0 && !isLoading ? (
              <p className="text-sm text-gray-500">{tr("Checklists none")}</p>
            ) : null}
            {displayRows.map((row: ChecklistRow) => {
              const isPending = row.entry_id == null;
              const hasPatch =
                row.entry_id != null &&
                (row.label !== row.originalLabel ||
                  row.required !== row.originalRequired);
              const showDismissPending = isPending;
              const showUndoPatch = !isPending && hasPatch;

              return (
                <div
                  key={row.key}
                  className="flex flex-wrap items-center gap-2 border-b border-gray-100 pb-2 last:border-0"
                >
                  <Input
                    value={row.label ?? ""}
                    onChange={(ev: React.ChangeEvent<HTMLInputElement>) =>
                      handleLabelChange(row, ev.target.value)
                    }
                    disabled={readOnly}
                    style={{ flex: "1 1 200px", minWidth: 0 }}
                  />
                  <label className="flex items-center gap-1 text-sm whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={row.required === true}
                      onChange={(ev) => handleRequiredChange(row, ev.target.checked)}
                      disabled={readOnly}
                    />
                    {tr("Checklists required")}
                  </label>
                  {!readOnly ? (
                    <IconButton
                      type="button"
                      onClick={() => {
                        if (showUndoPatch) {
                          handleUndoPatchesForRow(row);
                          return;
                        }
                        handleDismissRow(row);
                      }}
                      aria-label={
                        showDismissPending
                          ? tr("Checklists dismiss pending row")
                          : showUndoPatch
                            ? tr("Checklists undo row changes")
                            : tr("Checklists remove")
                      }
                      title={
                        showDismissPending
                          ? tr("Checklists dismiss pending row")
                          : showUndoPatch
                            ? tr("Checklists undo row changes")
                            : tr("Checklists remove")
                      }
                      style={
                        showDismissPending
                          ? { color: "#6b7280" }
                          : showUndoPatch
                            ? undefined
                            : { color: "#dc2626" }
                      }
                      disabled={isLoading}
                    >
                      {showDismissPending ? (
                        <RiCloseCircleFill size={22} />
                      ) : showUndoPatch ? (
                        <CgUndo size={24} className="text-blue-500" />
                      ) : (
                        <RiDeleteBin2Fill size={20} />
                      )}
                    </IconButton>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : null}
      </div>
      {!readOnly && Number.isFinite(typeNum) ? (
        <div className="mt-2 relative" ref={templatePickerAnchorRef}>
          <OutlineButton type="button" onClick={() => setTemplatePickerOpen((o) => !o)}>
            {tr("Checklists add existing")}
          </OutlineButton>
          <ChecklistExistingPicker
            open={templatePickerOpen}
            onOpenChange={setTemplatePickerOpen}
            anchorRef={templatePickerAnchorRef}
            appointmentTypeId={typeNum}
            subAppointmentTypeId={defaultNum}
            variant="appointment"
            onConfirm={handleConfirmTemplatesFromPicker}
          />
        </div>
      ) : null}
    </div>
  );
}
